import { ExternalTracker } from '@/@types/graphql-types';
import { TrackingHelper } from './tracking/tracking-helper';
import { captureMessageForSentry } from './sentry-helper';
import { ImpressionTrackingEvents } from '@/enums/events';
import { TrackingProviderPropertiesInterface } from './tracking/providers';

const MACRO_TIMESTAMP = /\[(timestamp)]/i;
const MACRO_GDPR_CONSENT = '${GDPR}' as const;

type FormatMacroImpressionType = {
	tracker: string;
	hasConsent?: boolean;
};

export type ExternalTrackerFiredType = 'external_impression_tracker_fired' | 'external_clickout_tracker_fired';

/**
 *
 * Formatting an impression tracker, that may include impressions or clickout URL
 *
 * At the moment, the trackers are only fired if consent is given,
 * so we can safely assume that this replacement is set to '1'.
 *
 * @param impression {number} a URL with the included [timestamp] macro
 * @param hasConsent {boolean} if user has given GDPR consent
 *
 * @returns a formatted tracker
 */

export const formatExternalTracker = ({ tracker, hasConsent = true }: FormatMacroImpressionType): string => {
	if (!tracker.length) return '';

	tracker = tracker.replace(MACRO_TIMESTAMP, Date.now().toString());
	tracker = tracker.replace(MACRO_GDPR_CONSENT, hasConsent ? '1' : '0');

	return tracker;
};

/**
 * Loop through all our external tracker urls, allowing us to
 * have multiple trackers
 *
 * @param externalTrackers
 */
export const handleExternalClickoutTrackers = ({
	externalTrackers,
	trackerType,
	bidId,
}: {
	externalTrackers: ExternalTracker[];
	trackerType: ExternalTrackerFiredType;
	bidId: string;
}) => {
	externalTrackers.forEach(async ({ data }) => {
		if (data) {
			// TODO: if env variable is not production, handle the URLs differently
			const url = formatExternalTracker({
				tracker: data,
			});

			/**
			 * At the moment we cannot get the status due to no cors
			 * So we will manually say confirmed for now
			 */
			let status = 200;

			try {
				await fetch(url, {
					mode: 'no-cors',
				});
			} catch (err: any) {
				captureMessageForSentry(
					'[Unable to send the external tracker]:',
					{ error: err, where: 'Component: External tracker' },
					'error'
				);

				status = 418;
			}

			const properties: TrackingProviderPropertiesInterface = {
				action: trackerType,
				label: bidId,
				property: data,
				value: status,
			};

			TrackingHelper.trackEvent(ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS, properties);
		}
	});
};

/**
 * Inject external HTML trackers into the body of the page
 *
 * @param externalTrackers
 */
export const injectExternalHtmlTrackers = (externalTrackers: ExternalTracker[]) => {
	externalTrackers.forEach(({ data }) => {
		if (data) {
			const formatImpression = formatExternalTracker({ tracker: data });
			const fragment = document.createRange().createContextualFragment(formatImpression);

			document.head.appendChild(fragment);
		}
	});
};
